import React, { useCallback, useMemo } from 'react';

import { useFormContext } from 'react-hook-form';

import { Button, FontAwesomeIcon } from '@appcharge/shared-ui';
import { EBadgeType, Offer, RollingOfferProductSequence } from 'common/contracts';
import { motion } from 'framer-motion';
import { RollingOfferFormFields } from 'views/Offers/RollingOffers/Form/RollingOfferForm.consts';

import DialogModal from 'components/DialogModal/DialogModal';
import { cn } from 'components/lib/utils';
import { Collapsible, CollapsibleTrigger } from 'components/ui/collapsible';

import CardContent from './CardContent';

interface ISubOfferCardProps {
  rollingOfferId?: string;
  productSequenceItem: RollingOfferProductSequence;
  sequenceIndex: number;
  productsSequence: RollingOfferProductSequence[];
  setProductsSequence: React.Dispatch<
    React.SetStateAction<RollingOfferProductSequence[]>
  >;
  setBarPointsValidity?: React.Dispatch<
    React.SetStateAction<Record<number, boolean>>
  >;
  relevantBadgeType?: EBadgeType;
  isExpanded: boolean;
  isCurrentItemDragged?: boolean;
  onToggleExpand: () => void;
  onDelete: () => void;
  dragHandleListeners?: Record<string, unknown>;
  dragHandleAttributes?: Record<string, unknown>;
  progressBarOffers?: Offer[];
}

const SubOfferCard: React.FC<ISubOfferCardProps> = ({
  productSequenceItem,
  sequenceIndex,
  setProductsSequence,
  setBarPointsValidity,
  relevantBadgeType,
  isExpanded,
  isCurrentItemDragged = false,
  onToggleExpand,
  onDelete,
  dragHandleListeners,
  dragHandleAttributes,
  progressBarOffers
}): React.ReactElement => {
  const { setValue, watch } = useFormContext<RollingOfferFormFields>();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);

  const offerName = watch('name') || '';

  const collapseIcon = useMemo(
    (): string => {
      if (isExpanded) {
        return 'fa-chevron-up';
      }
      return 'fa-chevron-down';
    },
    [isExpanded]
  );

  const cardTitle = `Sub Offer #${sequenceIndex + 1}`;

  const handleOpenChange = useCallback(
    (open: boolean): void => {
      if (open !== isExpanded) {
        onToggleExpand();
      }
    },
    [isExpanded, onToggleExpand]
  );

  const updateSubOfferData = useCallback(
    (updatedData: Partial<RollingOfferProductSequence>): void => {
      setProductsSequence((prevSequence: RollingOfferProductSequence[]) => {
        const updatedSequence = [...prevSequence];
        updatedSequence[sequenceIndex] = {
          ...updatedSequence[sequenceIndex],
          ...updatedData
        };
        setValue('productsSequence', updatedSequence, { shouldDirty: true });
        return updatedSequence;
      });
    },
    [sequenceIndex, setProductsSequence, setValue]
  );

  const handleClickDelete = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>): void => {
      e.preventDefault();
      e.stopPropagation();
      setIsDeleteDialogOpen(true);
    },
    []
  );

  const handleConfirmDelete = useCallback((): void => {
    onDelete();
    setIsDeleteDialogOpen(false);
  }, [onDelete]);

  const handleCancelDelete = useCallback((): void => {
    setIsDeleteDialogOpen(false);
  }, []);

  const handleDeleteDialogChange = useCallback(
    (open: boolean): void => {
      if (!open) {
        setIsDeleteDialogOpen(false);
      }
    },
    []
  );

  return (
    <div className="flex flex-col">
      <Collapsible
        className={cn('group/collapsible')}
        open={isExpanded}
        onOpenChange={handleOpenChange}
      >
        <div
          className={cn(
            'flex flex-col border rounded-lg transition-colors bg-white border-base-sidebarBorder hover:border-indigo-500'
          )}
        >
          <CollapsibleTrigger className="group flex justify-between items-center w-full h-[61px] px-4 text-base-mutedForeground text-xs">
            <div
              className={cn(
                'leading-5 font-sans font-medium flex items-center gap-3',
                isExpanded && 'text-indigo-600'
              )}
            >
              <span
                {...(dragHandleListeners || {})}
                {...(dragHandleAttributes || {})}
                className={cn(
                  'inline-flex items-center',
                  'cursor-grab active:cursor-grabbing'
                )}
              >
                <FontAwesomeIcon
                  icon="fa-solid fa-grip-dots-vertical"
                  className={cn(
                    'text-md transition-opacity w-[12px] text-base-mutedForeground group-hover/collapsible:text-base-foreground',
                    (isCurrentItemDragged || isExpanded) &&
                      'text-base-foreground'
                  )}
                />
              </span>
              <span className="text-base-foreground font-medium text-sm">
                {cardTitle}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={handleClickDelete}
                className={cn(
                  'p-1 rounded opacity-0 group-hover/collapsible:opacity-100 hover:bg-gray-50 w-10 h-10 flex items-center justify-center transition-opacity shadow-none'
                )}
                title="Delete sub offer"
              >
                <FontAwesomeIcon
                  icon="fa-regular fa-trash-can"
                  className="text-sm text-base-foreground"
                />
              </Button>
              <FontAwesomeIcon
                icon={`fa-solid ${collapseIcon}`}
                className={cn(
                  'text-xs text-base-mutedForeground h-10 w-10 flex items-center justify-center'
                )}
              />
            </div>
          </CollapsibleTrigger>

          <motion.div
            layout
            initial={{ height: 0 }}
            animate={{ height: isExpanded ? 'auto' : 0 }}
            transition={{ duration: 0.15, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <CardContent
              productSequenceItem={productSequenceItem}
              sequenceIndex={sequenceIndex}
              offerName={offerName}
              updateSubOfferData={updateSubOfferData}
              setBarPointsValidity={setBarPointsValidity}
              relevantBadgeType={relevantBadgeType}
              progressBarOffers={progressBarOffers}
            />
          </motion.div>
        </div>
      </Collapsible>

      {isDeleteDialogOpen && (
        <DialogModal
          isOpen={isDeleteDialogOpen}
          dataTestId="SubOfferCard-DeleteModal"
          onOpenChange={handleDeleteDialogChange}
          title="Delete Sub Offer"
          description="Are you sure you want to delete this sub offer? This action can't be undone."
          modalActions={{
            onConfirm: {
              label: 'Delete',
              action: handleConfirmDelete
            },
            onClose: {
              label: 'Cancel',
              action: handleCancelDelete,
              variant: 'link'
            }
          }}
        />
      )}
    </div>
  );
};

export default SubOfferCard;
